import React, { ReactElement } from 'react'
import { Box } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'

import { TelephoneIcon } from '~assets/icons'
import { Button, InputLabel, PhoneEntryBox, RemoveEntryButton } from '~components/common'
import { PhoneNumber0 } from '~types'

interface PhoneInputRow {
  id: string
  phoneNumber: string
  isValid: boolean
}

type PhoneInputTableProps = {
  setPhoneNumbers: React.Dispatch<React.SetStateAction<PhoneNumber0[]>>
  onValidationChange?: (isValid: boolean) => void
}

const PhoneInputTable = ({
  setPhoneNumbers,
  onValidationChange,
}: PhoneInputTableProps): ReactElement => {
  const [inputRows, setInputRows] = React.useState<PhoneInputRow[]>([
    { id: uuidv4(), phoneNumber: '', isValid: false },
  ])

  const _areAllPhonesValid = React.useMemo(() => {
    const isValid = inputRows.every((row) => row.isValid || row.phoneNumber === '')

    onValidationChange?.(isValid)
    return isValid
  }, [inputRows, onValidationChange])

  const addPhoneInput = (): void => {
    setInputRows((prev) => [...prev, { id: uuidv4(), phoneNumber: '', isValid: false }])
  }

  const removePhoneInput = (id: string): void => {
    setInputRows((prev) => prev.filter((row) => row.id !== id))
  }

  const handlePhoneChange = (id: string, newNumber: string, isValid: boolean): void => {
    setInputRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, phoneNumber: newNumber, isValid } : row)),
    )

    const validPhones = inputRows
      .map((row) => ({
        id: row.id === id ? id : row.id,
        phoneNumber: row.id === id ? newNumber : row.phoneNumber,
        isValid: row.id === id ? isValid : row.isValid,
      }))
      .filter((row) => row.isValid)
      .map((row) => ({
        phone_number_id: row.id,
        contact_id: row.id,
        phone_number: row.phoneNumber,
      }))

    setPhoneNumbers(validPhones)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
          mb: 2,
          borderBottom: '1px solid #ccc',
        }}
      >
        <InputLabel label='Phones' isOptional icon={TelephoneIcon} />

        {/* Phone input rows */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {inputRows.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                gap: 1,
                mr: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <PhoneEntryBox
                  inputPhoneState={row.phoneNumber}
                  onChange={(newNumber, newIsValid) =>
                    handlePhoneChange(row.id, newNumber, newIsValid)
                  }
                />
              </Box>
              <Box sx={{ mt: 1 }}>
                <RemoveEntryButton
                  row={row}
                  onRemove={() => removePhoneInput(row.id)}
                  showWarning={false}
                  showConfirmation={false}
                />
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Box mr={8}>
              <Button size='small' variant='outlined' color='secondary' onClick={addPhoneInput}>
                Add
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PhoneInputTable
